import { EventTrigger, EventTriggerType } from "@app/shared/src/models";
import { sideOfZoneName } from "@app/shared/src/helpers/zoneNameHelpers";

/**
 * The four alerts a TMA raises, as the console talks about them.
 *
 * Lives apart from the hooks that use it so pure helpers can categorise a trigger without importing
 * React. Impending collision and the two side passes are all impending-entry triggers; what tells
 * them apart is the zone each watches, which is why this goes through sideOfZoneName rather than
 * inspecting the trigger type alone.
 */
export type AlertCategory = "collision" | "speeding" | "left" | "right";

export const ALERT_CATEGORIES: AlertCategory[] = ["collision", "speeding", "left", "right"];

export const ALERT_CATEGORY_LABELS: Record<AlertCategory, string> = {
    collision: "Impending collision",
    speeding: "Speeding",
    left: "Left-side pass",
    right: "Right-side pass",
};

export function categoriseTrigger(t: EventTrigger): AlertCategory | undefined {
    if (t.triggerType === EventTriggerType.SpeedingEvent) return "speeding";
    if (t.triggerType === EventTriggerType.ImpendingZoneEntry) {
        return sideOfZoneName(t.detectionZone?.name) ?? "collision";
    }
    return undefined;
}

/** The one trigger of each kind on a site, keyed for lookup. */
export function triggersByCategory(triggers: EventTrigger[]): Partial<Record<AlertCategory, EventTrigger>> {
    const map: Partial<Record<AlertCategory, EventTrigger>> = {};
    for (const t of triggers) {
        const c = categoriseTrigger(t);
        if (c && !map[c]) map[c] = t;
    }
    return map;
}
